import React from 'react'
import { useSelector } from 'react-redux'
import { toPng } from 'html-to-image'
import { Download, Loader2 } from 'lucide-react'
import {
  getWorldCup2026TeamFlag,
  isWorldCup2026League,
} from '../../constants/worldCup2026TeamFlags'
import type { NewsMatchCard, OutcomeSide } from '../../services/newsService'
import { useTranslation } from '../../hooks/use-i18n'
import type { RootState } from '../../store'
import { canDownloadNewsPosters } from '../../utils/admin'

const SIDE_DOT: Record<OutcomeSide, string> = {
  teamA: 'bg-blue-500',
  draw: 'bg-slate-400',
  teamB: 'bg-rose-500',
}
const SIDE_BAR: Record<OutcomeSide, string> = {
  teamA: 'bg-blue-500',
  draw: 'bg-slate-400',
  teamB: 'bg-rose-500',
}
const SIDE_TEXT: Record<OutcomeSide, string> = {
  teamA: 'text-blue-500',
  draw: 'text-slate-400',
  teamB: 'text-rose-500',
}
const SIDE_RING: Record<OutcomeSide, string> = {
  teamA: 'border-blue-500/60',
  draw: 'border-slate-400/60',
  teamB: 'border-rose-500/60',
}

const flagFor = (league: string | null, name: string): string | null =>
  isWorldCup2026League(league) ? getWorldCup2026TeamFlag(name) : null

interface Props {
  card: NewsMatchCard
}

const NewsPredictionCard: React.FC<Props> = ({ card }) => {
  const { t } = useTranslation()
  const { teamA, teamB, winner, scorePredictions, league } = card
  const flagA = flagFor(league, teamA.name)
  const flagB = flagFor(league, teamB.name)
  const maxVotes = scorePredictions.length ? scorePredictions[0].votes : 0

  // Show every score prediction on all viewports (mobile included).
  const grid = scorePredictions
  const bars = scorePredictions

  const user = useSelector((state: RootState) => state.auth.user)
  // Download is restricted to one account and only for upcoming-game posters.
  const canDownload = canDownloadNewsPosters(user) && card.status === 'upcoming'
  const cardRef = React.useRef<HTMLElement>(null)
  const [downloading, setDownloading] = React.useState(false)

  const handleDownload = async () => {
    const node = cardRef.current
    if (!node || downloading) return
    setDownloading(true)
    try {
      // High-quality capture: render at 3x and skip the toolbar (the button itself).
      const dataUrl = await toPng(node, {
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: getComputedStyle(node).backgroundColor || '#0f172a',
        filter: (el) =>
          !(el instanceof HTMLElement && el.dataset.exportExclude === 'true'),
      })
      const slug = `${teamA.name}-vs-${teamB.name}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
      const link = document.createElement('a')
      link.download = `breakroomcup-${slug || card.matchId}.png`
      link.href = dataUrl
      link.click()
    } catch (err) {
      console.error('Poster download failed:', err)
    } finally {
      setDownloading(false)
    }
  }

  const sideName = (side: OutcomeSide) =>
    side === 'draw' ? t('news.draw') : side === 'teamA' ? teamA.name : teamB.name
  const winnerLabel = (side: OutcomeSide) =>
    side === 'draw' ? t('news.draw') : side === 'teamA' ? teamA.name : teamB.name
  const fmtScore = (score: string) => (score === '5andmore' ? t('news.scoreMore') : score)
  const fmtScoreShort = (score: string) => (score === '5andmore' ? '5+' : score)

  return (
    <article ref={cardRef} className="relative bg-card border border-border shadow-lg rounded-2xl p-3 sm:p-6">
      {canDownload && (
        <button
          type="button"
          data-export-exclude="true"
          onClick={handleDownload}
          disabled={downloading}
          title={t('news.download')}
          aria-label={t('news.download')}
          className="absolute top-2 right-2 z-10 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold shadow hover:opacity-90 disabled:opacity-60"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          <span className="hidden sm:inline">{downloading ? t('news.downloading') : t('news.download')}</span>
        </button>
      )}
      {/* Header */}
      <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground">
        <span className="font-semibold text-primary">{card.leagueLabel}</span>
        <span>
          {card.dateLabel} · {card.timeLabel}
        </span>
      </div>
      <div className="h-px bg-border my-2 sm:my-3" />

      {card.aiBrief && (
        <div className="mb-3 rounded-xl bg-primary/5 border border-primary/15 px-3 py-2.5 text-sm text-foreground leading-relaxed">
          <span className="text-[10px] uppercase tracking-wide text-primary font-semibold mr-2">AI</span>
          {card.aiBrief}
        </div>
      )}

      {/* Match predictions */}
      <p className="text-center text-[11px] font-semibold tracking-widest text-muted-foreground uppercase mb-2 sm:mb-3">
        {t('news.matchPredictions')}
      </p>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0 text-center">
          <div className="flex items-center justify-center gap-2">
            {flagA && <span className="text-3xl sm:text-5xl leading-none">{flagA}</span>}
            <span className="text-lg sm:text-2xl font-extrabold text-foreground truncate">{teamA.name}</span>
          </div>
          <p className="text-xs text-blue-500 mt-0.5 sm:mt-1">
            {t('news.predictionsPct', { count: String(winner.teamA.votes), pct: String(winner.teamA.pct) })}
          </p>
        </div>
        <span className="shrink-0 px-2.5 py-1 rounded-full bg-muted/40 text-xs font-bold text-muted-foreground">
          {t('news.vs')}
        </span>
        <div className="flex-1 min-w-0 text-center">
          <div className="flex items-center justify-center gap-2">
            <span className="text-lg sm:text-2xl font-extrabold text-foreground truncate">{teamB.name}</span>
            {flagB && <span className="text-3xl sm:text-5xl leading-none">{flagB}</span>}
          </div>
          <p className="text-xs text-rose-500 mt-0.5 sm:mt-1">
            {t('news.predictionsPct', { count: String(winner.teamB.votes), pct: String(winner.teamB.pct) })}
          </p>
        </div>
      </div>

      {/* Winner split pills */}
      <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 mt-2.5 sm:mt-4">
        {([winner.teamA, winner.draw, winner.teamB] as const).map((w) => (
          <span
            key={w.side}
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-border text-xs font-medium text-foreground"
          >
            <span className={`w-2 h-2 rounded-full ${SIDE_DOT[w.side]}`} />
            <span className="truncate max-w-[120px]">{winnerLabel(w.side)}</span>
            <span className="font-bold">{w.votes}</span>
          </span>
        ))}
        <span className="text-xs text-muted-foreground ml-1">{t('news.total', { count: String(card.totalPredictions) })}</span>
      </div>

      {card.result && (
        <div className="mt-4 text-center text-sm">
          <span className="inline-block px-3 py-1 rounded-lg bg-primary/15 text-primary font-semibold">
            {t('news.finalResult', {
              result: `${card.result.score || 'N/A'}${card.result.winner ? ` · ${card.result.winner}` : ''}`,
            })}
          </span>
        </div>
      )}

      {grid.length > 0 && (
        <>
          <p className="text-center text-[11px] font-semibold tracking-widest text-muted-foreground uppercase mt-3 sm:mt-6 mb-1.5 sm:mb-3">
            {t('news.scorePredictions')}
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 sm:gap-3">
            {grid.map((s, i) => (
              <div
                key={s.score}
                className={`rounded-lg sm:rounded-xl border p-1.5 sm:p-3 text-center bg-muted/40 ${
                  i === 0 ? SIDE_RING[s.side] : 'border-border'
                }`}
              >
                <div className={`text-sm sm:text-xl font-bold leading-tight ${SIDE_TEXT[s.side]}`}>{fmtScore(s.score)}</div>
                <div className="text-[10px] sm:text-[11px] text-muted-foreground truncate">{sideName(s.side)}</div>
                <div className="text-[10px] sm:text-xs font-semibold text-foreground mt-0.5">
                  {s.votes} <span className="font-normal text-muted-foreground">{t('news.votes')}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {grid.length > 0 && (
        <div>
          <p className="text-center text-[11px] font-semibold tracking-widest text-muted-foreground uppercase mt-3 sm:mt-6 mb-1.5 sm:mb-2">
            {t('news.voteDistribution')}
          </p>
          <div className="flex items-center justify-center gap-3 sm:gap-4 text-[11px] text-muted-foreground mb-2 sm:mb-3">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> {t('news.win', { team: teamA.name })}</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" /> {t('news.draw')}</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> {t('news.win', { team: teamB.name })}</span>
          </div>
          <div className="space-y-1 sm:space-y-1.5">
            {bars.map((s) => (
              <div key={s.score} className="flex items-center gap-2">
                <span className="w-9 shrink-0 text-xs text-muted-foreground text-right">{fmtScoreShort(s.score)}</span>
                <div className="flex-1 h-4 sm:h-5 rounded bg-muted/50 overflow-hidden">
                  <div
                    className={`h-full rounded ${SIDE_BAR[s.side]}`}
                    style={{ width: `${maxVotes > 0 ? Math.max(2, (s.votes / maxVotes) * 100) : 0}%` }}
                  />
                </div>
                <span className="w-8 shrink-0 text-xs font-semibold text-foreground">{s.votes}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </article>
  )
}

export default NewsPredictionCard
